import { useCallback, useEffect, useRef, useState } from "react";
import { fxOverviewLaden, type FxOverview } from "../../api/kurssicherung.ts";
import { SitzungsFehler } from "../../api/basis.ts";
import { hatRecht } from "../../state/berechtigungen.ts";
import { meldeFehler, meldeNachladeFehler } from "../../state/fehlerStapel.ts";
import type { Nutzer } from "../../state/reduzierer.ts";
import type { FxDeal } from "../../lib/fx.ts";
import { LIQUIDITAET_RECHT, LIQUIDITAET_SEITE, type AktiveSeite } from "./Navigation.tsx";

/**
 * Die Kurssicherungsuebersicht (`/api/fx-overview`) braucht die Zahlungsseite (Konten, Register) und
 * die Liquiditaetsplanung (USD-Kontostand).
 */
function seiteBrauchtFxOverview(seite: AktiveSeite): boolean {
  return seite === "payments" || seite === LIQUIDITAET_SEITE;
}

/**
 * Der feste Satz fuer ein Laden, das der Mensch ausgeloest hat (Seite oeffnen, Knopf) und das bei vorhandenem Stand scheitert
 * (L3): neben dem Eintrag stehen sichtbar die Daten des vorigen Besuchs, also sagt er, dass sie der vorige Stand sind. Wortlaut
 * wie `NEULADEN_GESCHEITERT` in `state/Speicher.tsx`. Bewusst NICHT "... nicht aktualisiert werden": das ist der Wortlaut des
 * R4-Falls (Schreiben gelang), den der Waechter `r4-falscher-helfer` ueber `meldeFehler` verbietet. Hier wurde nichts geschrieben,
 * der Rat der Ursache stimmt, `meldeFehler` ist richtig.
 */
const ALTSTAND_SATZ = "Die Kurssicherungsübersicht konnte nicht neu geladen werden und zeigt noch den vorigen Stand.";
/** Der feste Satz fuer ein Nachladen im Hintergrund (L4), das bei vorhandenem Stand scheitert: der Stand ist nicht mehr aktuell. */
const HINTERGRUND_SATZ = "Die Kurssicherungsübersicht konnte nicht aktualisiert werden. Bitte neu laden.";

/**
 * Laedt die Kurssicherungsuebersicht und sagt, wenn das scheitert (Fehleranzeige Paket 2, Task 9; Spec "Nachladen nach einem
 * Schreiben"). Drei Faelle, je nachdem, ob schon ein Stand da ist und wer geladen hat:
 *
 * - **Noch kein Stand (L1):** `fxOverviewLadefehler` wird wahr, `fxOverviewLadeursache` traegt den Grund; die Flaeche in
 *   `KurssicherungsUebersicht` und die Zeile in `LiquiditaetsSeite` zeigen ihn samt "Erneut laden". KEIN Eintrag im
 *   Fehlerstapel (sonst stuende derselbe Fehler
 *   zweimal da).
 * - **Schon ein Stand (L3, L4):** Der Stand BLEIBT; fruehere Fassung verwarf ihn bei jedem Fehlschlag (`setFxOverview(null)`).
 *   Eine Flaeche gibt es dann nicht (sie verdeckte Daten, die da sind), sondern einen Eintrag: im Hintergrund
 *   (`hintergrund`) `HINTERGRUND_SATZ`, sonst (Seite oeffnen, Knopf) `ALTSTAND_SATZ` samt Ursache.
 * - **Nach einem Schreiben (R4, `fxOverviewNachSchreibenLaden`):** Die Aktion ist GELUNGEN, nur die Anzeige ist veraltet. Der
 *   Eintrag ist der Satz der Aktion ("Die Kurssicherung ist angelegt, die Übersicht konnte aber nicht aktualisiert werden.
 *   Bitte neu laden.") ueber `meldeNachladeFehler`: ohne den Wiederholungsrat der Ursache, denn "Bitte gleich noch einmal
 *   versuchen" hinter "ist angelegt" riefe zur Doppelanlage auf. Das ueberholt das Ruling R-Z2a (der Rat blieb nach dem
 *   Schreiben stehen, ein Toast bestaetigte den Erfolg; `docs/superpowers/reference/2026-10-20-fehleranzeige-offene-punkte.md`).
 *
 * Kein Abruf lehnt ab. Jeder Aufrufer bekommt ein Promise, das IMMER erfuellt wird (die Flaeche und die Dialoge haengen daran,
 * `Ladefehler/Ladefehler.tsx`).
 */
export function useFxOverview(
  nutzer: Nutzer | null,
  seite: AktiveSeite,
  rechte: ReadonlySet<string>,
  fxDeals: FxDeal[],
) {
  const [fxOverview, setFxOverview] = useState<FxOverview | null>(null);
  // Der Ladefehler OHNE Stand: das Fehlerobjekt selbst, eingewickelt, weil auch `undefined` ein geworfener Wert sein kann.
  // `null` heisst: kein Ladefehler.
  const [ladefehler, setLadefehler] = useState<{ ursache: unknown } | null>(null);
  const fxOverviewLadefolge = useRef(0);
  // Ob schon ein Stand uebernommen ist - der Fehlerweg entscheidet daran zwischen Flaeche und Eintrag und liest es aus einem
  // Callback mit leeren Abhaengigkeiten, der den State nicht sieht.
  const standDa = useRef(false);
  // Die Saetze gelungener Schreibaktionen, deren Nachladen noch aussteht. Ein Schreiben aendert meist auch `fxDeals`; der
  // Effekt unten startet dann einen Hintergrundlauf, der den Lauf der Aktion UEBERHOLT (`fxOverviewLadefolge`). Scheitern beide,
  // meldet nur der letzte - und der soll den Satz der Aktion tragen, nicht nur "nicht aktualisiert": sonst weiss der Nutzer
  // nicht, dass die Sicherung angelegt ist. Ein gelungener Lauf leert die Menge: die Anzeige ist dann aktuell.
  const offeneSaetze = useRef<Set<string>>(new Set());
  const fxOverviewZugriffBeiRenderZeit = useRef({ nutzer, seite });
  fxOverviewZugriffBeiRenderZeit.current = { nutzer, seite };

  // `hintergrund`: die Anwendung laedt von selbst nach, der Mensch hat nichts getan
  // (`AnfrageOptionen.hintergrund`). Der oeffentliche `fxOverviewNeuLaden` unten ist dagegen immer die
  // Folge einer Handlung des Menschen und bleibt parameterlos (er wird als `onOverviewNeuLaden` an
  // eine Komponente gereicht, ein Ereignisargument duerfte nie als `hintergrund` gelesen werden).
  // `nachSchreibenSatz` kommt nur von `fxOverviewNachSchreibenLaden`: ein eigener Weg mit eigenem Namen aus demselben Grund.
  const fxOverviewLadenMitArt = useCallback(async (hintergrund: boolean, nachSchreibenSatz?: string): Promise<void> => {
    const aktuellerZugriff = fxOverviewZugriffBeiRenderZeit.current;
    if (!aktuellerZugriff.nutzer || !seiteBrauchtFxOverview(aktuellerZugriff.seite)) return;
    if (nachSchreibenSatz !== undefined) offeneSaetze.current.add(nachSchreibenSatz);
    const folge = ++fxOverviewLadefolge.current;
    try {
      const neu = await fxOverviewLaden(hintergrund);
      if (folge !== fxOverviewLadefolge.current) return;
      offeneSaetze.current.clear();
      standDa.current = true;
      setFxOverview(neu);
      setLadefehler(null);
    } catch (fehler) {
      if (folge !== fxOverviewLadefolge.current) return;
      const saetze = [...offeneSaetze.current];
      offeneSaetze.current.clear();
      if (fehler instanceof SitzungsFehler) return;
      // R4: jede gelungene Aktion, deren Anzeige jetzt veraltet bleibt, sagt das mit ihrem eigenen Satz. Das gilt auch ohne
      // Stand: die Flaeche unten sagt nur, dass die Uebersicht fehlt, nicht, dass das Schreiben gelang.
      for (const satz of saetze) meldeNachladeFehler(fehler, satz);
      if (!standDa.current) {
        // L1: die Flaeche traegt den Fehler allein. Kein `meldeFehler` daneben.
        setLadefehler({ ursache: fehler });
        return;
      }
      // L3/L4: der Stand bleibt stehen. Hat eine Aktion ihren Satz gemeldet, sagt der schon alles.
      if (saetze.length > 0) return;
      if (hintergrund) meldeNachladeFehler(fehler, HINTERGRUND_SATZ);
      else meldeFehler(fehler, ALTSTAND_SATZ);
    }
  }, []);

  const fxOverviewNeuLaden = useCallback(
    (): Promise<void> => fxOverviewLadenMitArt(false),
    [fxOverviewLadenMitArt],
  );

  /**
   * Das Nachladen NACH einem gelungenen Schreiben (Anlegen, Loeschen, Uebernehmen einer Sicherung, Kontostand erfassen).
   * `satz` sagt, was gespeichert ist und dass die Anzeige nicht aktuell ist; er kommt nur zum Zug, wenn das Nachladen
   * scheitert. Ein eigener Weg statt eines Parameters an `fxOverviewNeuLaden`, weil dieser parameterlos bleiben muss (siehe oben).
   */
  const fxOverviewNachSchreibenLaden = useCallback(
    (satz: string): Promise<void> => fxOverviewLadenMitArt(false, satz),
    [fxOverviewLadenMitArt],
  );

  // Die Eingaben des vorigen Effektlaufs - IMMER fortgeschrieben, auch wenn der Lauf unten vorzeitig
  // abbricht, damit "hat nur fxDeals gewechselt" gegen den wirklich letzten Stand misst.
  const vorherigeEingaben = useRef<{
    nutzerId: string | undefined;
    fxDeals: FxDeal[];
    rechte: ReadonlySet<string>;
    seite: AktiveSeite;
    laden: typeof fxOverviewLadenMitArt;
  } | null>(null);

  useEffect(() => {
    // "Aktiv" heisst: der Mensch hat etwas getan. Oeffnet er die Seite (oder wechseln Nutzer/Rechte),
    // ist der Abruf seine Handlung. Hat allein `fxDeals` gewechselt (SSE-Nachladen), laedt die
    // Anwendung von selbst nach: Hintergrundanfrage, zaehlt nicht als Aktivitaet.
    const vorher = vorherigeEingaben.current;
    vorherigeEingaben.current = { nutzerId: nutzer?.id, fxDeals, rechte, seite, laden: fxOverviewLadenMitArt };
    const nurFxDealsGewechselt =
      vorher !== null &&
      vorher.fxDeals !== fxDeals &&
      vorher.nutzerId === nutzer?.id &&
      vorher.rechte === rechte &&
      vorher.seite === seite &&
      vorher.laden === fxOverviewLadenMitArt;
    if (!nutzer) {
      fxOverviewLadefolge.current += 1;
      offeneSaetze.current.clear();
      standDa.current = false;
      setFxOverview(null);
      setLadefehler(null);
      return;
    }
    // Die Liquiditaetsplanung laedt nur MIT Recht: ohne `view_payments` springt der Rechte-Rueckfall
    // unten zwar zurueck, der Effekt hier lief aber schon vorher und liesse einen 403-Abruf (samt
    // Fehlermeldung) raus. Die Zahlungsseite behaelt ihr bisheriges Verhalten.
    if (
      !seiteBrauchtFxOverview(seite) ||
      (seite === LIQUIDITAET_SEITE && !hatRecht(rechte, LIQUIDITAET_RECHT))
    ) {
      // Der laufende Abruf wird verworfen; ein Satz, der auf ihn wartete, gilt nicht mehr: die Seite ist weg, und beim
      // naechsten Oeffnen laedt sie ohnehin neu.
      fxOverviewLadefolge.current += 1;
      offeneSaetze.current.clear();
      return;
    }
    void fxOverviewLadenMitArt(nurFxDealsGewechselt);
  }, [nutzer?.id, fxDeals, rechte, seite, fxOverviewLadenMitArt]);

  function fxOverviewKontoAktualisieren(id: string, accountId: string): void {
    // Das wirksame Konto steht in der Uebersicht (bei Alt-Sicherungen bleibt die Rohspalte accountId des
    // Deals unveraendert): sofort dort nachziehen, sonst springt das Feld bis zum Neuladen zurueck.
    setFxOverview((alt) => alt && {
      ...alt,
      deals: alt.deals.map((d) => (String(d.id) === id ? { ...d, accountId } : d)),
    });
  }

  return {
    fxOverview,
    fxOverviewLadefehler: ladefehler !== null,
    fxOverviewLadeursache: ladefehler?.ursache,
    fxOverviewNeuLaden,
    fxOverviewNachSchreibenLaden,
    fxOverviewKontoAktualisieren,
  };
}
